import { useMutation, useQueryClient } from '@tanstack/react-query';

import { secretService } from '@/services/secret-service';
import type { SecretTemplate } from '@/types/core/secret-template';
import { pollUntilResourceNewerThan } from '@/utils/poll';
import type { UUID } from '@/utils/uuid';

import { secretMutationKeys, secretQueryKeys } from './query-keys';

/**
 * Hook to update an existing secret template.
 * Returns a react-query mutation that accepts updated template data.
 * Invalidates the secret templates list cache and the specific template cache on success.
 */
export function useUpdateSecretTemplate(tenantId?: UUID, templateId?: UUID) {
  const qc = useQueryClient();

  return useMutation({
    mutationKey: secretMutationKeys.updateSecretTemplate(tenantId, templateId),
    mutationFn: async (payload: {
      name: string;
      description?: string;
      schema: object;
    }) => {
      if (!tenantId) throw new Error('Missing tenant id');
      if (!templateId) throw new Error('Missing template id');

      const cachedTemplate = qc.getQueryData<SecretTemplate | null>(
        secretQueryKeys.templateDetail(tenantId, templateId)
      );
      const minRev = cachedTemplate?._rev ?? -2;

      await secretService.reconcileSecretTemplate(tenantId, {
        ...payload,
        existingTemplateId: templateId,
      });

      return pollUntilResourceNewerThan(
        () => secretService.findSecretTemplate(tenantId, templateId),
        minRev,
        { maxAttempts: 10, delayMs: 300 }
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: secretQueryKeys.templateList(tenantId),
        refetchType: 'all',
      });
      qc.invalidateQueries({
        queryKey: secretQueryKeys.templateDetail(tenantId, templateId),
        refetchType: 'all',
      });
    },
  });
}
